import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Btn } from "@/components/Btn";
import { ESSENTIAL_FEATURES, ADVANCED_ADDS, COMMERCE_ADDS } from "@/lib/data";

export default function Pricing() {
  return (
    <>
      <PageHero eyebrow="Pricing" title="Clear pricing." accent="No hidden costs."
        subtitle="Choose the level of functionality your business needs. Every website is a one-off build price — no quotes, no waiting, no surprises." />

      <section className="bg-paper text-ink">
        <div className="mx-auto max-w-[1280px] px-6 py-24 md:px-12 md:py-28">
          <div className="grid gap-6 lg:grid-cols-3">
            <Reveal>
              <div data-testid="pricing-essential" className="flex h-full flex-col border border-ink/12 bg-paper-cream p-10">
                <p className="text-[11px] uppercase tracking-label text-ink/60">Essential</p>
                <p className="mt-6 text-[10px] uppercase tracking-label text-ink/40">One-off build price</p>
                <p className="font-serif text-7xl">£895</p>
                <p className="mt-4 text-sm leading-relaxed text-ink/65">Everything you need for a beautiful, professional online presence. Ideal for independent businesses, trades, salons and consultants.</p>
                <ul className="mt-8 space-y-3">{ESSENTIAL_FEATURES.map((f) => (<li key={f} className="flex items-center gap-3 text-sm text-ink/75"><Check size={15} className="text-champagne" strokeWidth={2} />{f}</li>))}</ul>
                <div className="mt-auto pt-10"><Btn to="/start?package=website_essential" variant="primary" testid="pricing-essential-cta" className="w-full">Start my Website</Btn></div>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div data-testid="pricing-advanced" className="flex h-full flex-col bg-ink p-10 text-white">
                <p className="text-[11px] uppercase tracking-label text-champagne">Advanced</p>
                <p className="mt-6 text-[10px] uppercase tracking-label text-white/40">One-off build price</p>
                <p className="font-serif text-7xl">£1,395</p>
                <p className="mt-4 text-sm leading-relaxed text-white/70">A more powerful digital experience for businesses that need their website to do more — restaurants, hospitality and businesses requiring bookings or interactive content.</p>
                <p className="mt-8 text-sm text-white/70">Everything in Essential, plus:</p>
                <ul className="mt-4 space-y-3">{ADVANCED_ADDS.map((f) => (<li key={f} className="flex items-center gap-3 text-sm text-white/85"><Check size={15} className="text-champagne" strokeWidth={2} />{f}</li>))}</ul>
                <div className="mt-auto pt-10"><Btn to="/start?package=website_advanced" variant="gold" testid="pricing-advanced-cta" className="w-full">Start my Website</Btn></div>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div data-testid="pricing-commerce" className="flex h-full flex-col border border-ink/12 bg-paper-cream p-10">
                <p className="text-[11px] uppercase tracking-label text-ink/60">Commerce</p>
                <p className="mt-6 text-[10px] uppercase tracking-label text-ink/40">One-off build price</p>
                <p className="font-serif text-7xl">£1,995</p>
                <p className="mt-4 text-sm leading-relaxed text-ink/65">A complete ecommerce website for businesses ready to sell online — everything in the core website, plus a full online shop.</p>
                <p className="mt-8 text-sm text-ink/70">Everything in Advanced, plus:</p>
                <ul className="mt-4 space-y-3">{COMMERCE_ADDS.map((f) => (<li key={f} className="flex items-center gap-3 text-sm text-ink/75"><Check size={15} className="text-champagne" strokeWidth={2} />{f}</li>))}</ul>
                <div className="mt-auto pt-10"><Btn to="/start?package=website_commerce" variant="primary" testid="pricing-commerce-cta" className="w-full">Start my Website</Btn></div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-6 border border-ink/12 bg-paper-cream p-8">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <div><p className="text-[11px] uppercase tracking-label">Aftercare — £29 / month</p><p className="mt-2 text-sm text-ink/65">Hosting, SSL & security monitoring, routine maintenance, minor content updates and support.</p></div>
                <p className="text-[11px] uppercase tracking-label text-ink/60">First 3 months included · Month-to-month · Cancel anytime</p>
              </div>
            </div>
          </Reveal>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {[["Google Business Launch & Local SEO", "£175", "google_business"], ["Business Social Media Launch", "£175", "social_media"]].map(([t, p, pkg]) => (
              <Reveal key={pkg} delay={0.05}>
                <div className="flex items-center justify-between border border-ink/12 bg-paper-cream p-8">
                  <div><h3 className="text-[11px] uppercase tracking-label leading-relaxed">{t}</h3><p className="mt-2 font-serif text-3xl">{p}</p></div>
                  <Btn to={`/start?package=${pkg}`} variant="outline" dark arrow={false} testid={`pricing-${pkg}`}>Start</Btn>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-14 border-t border-ink/12 pt-8 text-center text-sm text-ink/55">
              Something outside these packages?{" "}
              <Link to="/start" data-testid="pricing-talk" className="text-ink underline underline-offset-4 transition-opacity hover:opacity-60">Talk to us →</Link>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
